import { useCallback, useEffect, useState } from "react";
import AccountsApi from "../../../api/AccountsApi";
import { useRecoilState } from "recoil";
import { memberState } from "../../../state/atoms";
import { AccountMemberDto, UpdateMemberDto } from "../../../generated/dashboard-client";
import "./UsersPage.scss";
import { success } from "../../../service/MessageService";
import EditMemberFieldModal, {
  MemberFieldType,
} from "../modal/EditMemberFieldModal";
import EditMemberEmailModal from "../modal/EditMemberEmailModal";
import EditMemberPasswordModal from "../modal/EditMemberPasswordModal";
import { handleServerError } from "../../../util/ErrorHandler";
import AccountMembersApi from "../../../api/AccountMembersApi";
import { Button, Skeleton } from "antd";

export function UsersPage() {
  const [member] = useRecoilState(memberState);
  const [loading, setLoading] = useState(true);

  const [owner, setOwner] = useState({} as AccountMemberDto);

  const [editModal, setEditModal] = useState(false);
  const [editEmailModal, setEditEmailModal] = useState(false);
  const [editPasswordModal, setEditPasswordModal] = useState(false);
  const [fieldValue, setFieldValue] = useState("");
  const [fieldType, setFieldType] = useState("no" as MemberFieldType);

  const fetchUserDetails = useCallback(() => {
    if (!member.id) return;

    setLoading(true);
    AccountsApi.getUserInfo(member.id).then((resp) => {
        setLoading(false);
        setOwner(resp.data || {});
      }).catch(() => {
        setLoading(false);
      });
  }, [member.id]);

  useEffect(fetchUserDetails, [fetchUserDetails]);

  function updateName() {
    setFieldValue(owner.name || member.name || "");
    setFieldType("name");
    setEditModal(true);
  }

  function updateEmail() {
    setFieldValue(owner.email || member.email || "");
    setFieldType("email");
    setEditEmailModal(true);
  }

  function updatePassword() {
    setFieldValue("");
    setFieldType("password");
    setEditPasswordModal(true);
  }

  function updateOwner(dto: UpdateMemberDto) {
    if (dto.email) owner.email = dto.email;
    if (dto.name) owner.name = dto.name;

    setOwner(owner);
  }

  function updateField(type: MemberFieldType, value: string) {
    const dto = {} as UpdateMemberDto;
    switch (type) {
      case "email":
        setEditEmailModal(false);
        fetchUserDetails();
        return;
      case "password":
        setEditPasswordModal(false);
        fetchUserDetails();
        return;
      case "name": {
        dto.name = value;
        AccountMembersApi.updateMember(member.id, dto)
          .then(() => {
            success("Successfully updated!");
            setEditModal(false);
            updateOwner(dto);
            fetchUserDetails();
          })
          .catch((e) => {
            handleServerError(e);
            setEditModal(false);
          });
        return;
      }
    }
  }

  return (
    <div className={"content users-page"}>
      {loading && <Skeleton active />}

      {!loading && (
        <>
          <section className={"owner"}>
            {member.owner && <h3>Account Owner</h3>}
            {!member.owner && <h3>Team Member</h3>}
            {owner.ssoMicrosoft ? (
              <div className={"sso-info"}>
                <ul>
                  <li>
                    <h5 className={"name-label"}>{owner.name || member.name}</h5>
                  </li>
                  <li>
                    <label>{owner.email || member.email}</label>
                  </li>
                </ul>
                <p className={"sso-notice"}>
                  Your account details are managed by your organization's Single Sign-On
                  (Microsoft Azure AD). Please contact your IT administrator to make changes.
                </p>
              </div>
            ) : (
              <ul>
                <li>
                  <h5 className={"name-label"}>{owner.name || member.name}</h5>
                  <Button className={"secondary"} onClick={updateName}>
                    Edit Name
                  </Button>
                </li>
                <li>
                  <label>{owner.email || member.email}</label>
                  <Button className={"secondary"} onClick={updateEmail}>
                    Edit Email
                  </Button>
                </li>
                <li>
                  <label>**********</label>
                  <Button className={"secondary"} onClick={updatePassword}>
                    Update Password
                  </Button>
                </li>
              </ul>
            )}
          </section>
        </>
      )}

      <EditMemberFieldModal
        visible={editModal}
        value={fieldValue}
        type={fieldType}
        onCancel={() => setEditModal(false)}
        onDone={updateField}
      />

      <EditMemberEmailModal
        visible={editEmailModal}
        value={fieldValue}
        type={fieldType}
        member={member}
        onCancel={() => setEditEmailModal(false)}
        onDone={updateField}
      />

      <EditMemberPasswordModal
        visible={editPasswordModal}
        value={fieldValue}
        type={fieldType}
        member={member}
        onCancel={() => setEditPasswordModal(false)}
        onDone={updateField}
      />
    </div>
  );
}
